import type { Period } from '@heidi/commerce/contracts';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCallback, useMemo } from 'react';
import { getStripeLink } from '../../../../data/api/billing';
import { useUserBillingInfo as useQueryUserBillingInfo } from '../../../../data/injected-billing';
import { getCommerceBillingUiPorts } from '../billing-ui-ports';

export type CancelTargetSubscription = {
  stripeSubscriptionId: string;
  productTier: SubscriptionTier;
};

type SubscriptionPortalPath = 'update' | 'cancel' | 'reactivate';

/** Portal pathname + subscription deeplink; query params preserved. */
export function buildBillingPortalSubscriptionDeeplink(
  billingPortalUrl: string,
  subscriptionId: string,
  path: SubscriptionPortalPath
): string {
  const url = new URL(billingPortalUrl);
  const pathname = url.pathname.replace(/\/$/, '');
  url.pathname = `${pathname}/subscriptions/${subscriptionId}/${path}`;
  return url.toString();
}

/**
 * What the portal's update-confirm should target. `price_id` names the price
 * directly, for plans with no tier of their own (Clinician Max, MONEY-845);
 * the two are mutually exclusive — the backend 422s on both.
 */
export type PortalUpdateTarget =
  | { tier: SubscriptionTier; period: Period }
  | { price_id: string };

type RedirectSubscriptionPortalOptions = {
  beforeNavigate?: () => void;
  target?: PortalUpdateTarget;
};

/** Caps click-to-redirect even if react-query retries after axios times out. */
export const BILLING_PORTAL_SESSION_TIMEOUT_MS = 15_000;

function withDeadline<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Billing portal session timed out'));
    }, timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timeoutId);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  });
}

/**
 * Billing portal deeplinks for users who already have a paid Stripe subscription (Scribe or Evidence).
 * Exposes subscription detection plus redirects to update or cancel in the customer portal.
 */
export function useUpdateExistingSubscription() {
  const router = getCommerceBillingUiPorts().useAppRouter();
  const { t } = useTranslations();
  const { data: billingInfo, isFetched: isBillingInfoLoaded } = useQueryUserBillingInfo();
  const { refetch: refetchStripeData } =
    getCommerceBillingUiPorts().useQueryStripeLink(false);

  const hasExistingEvidenceSubscription = useMemo(() => {
    return (
      Boolean(billingInfo?.evidence_billing?.stripe_subscription_id) &&
      !billingInfo?.evidence_billing?.is_in_free_trial &&
      billingInfo?.new_product_tier === SubscriptionTier.EVIDENCE
    );
  }, [
    billingInfo?.evidence_billing?.stripe_subscription_id,
    billingInfo?.evidence_billing?.is_in_free_trial,
    billingInfo?.new_product_tier,
  ]);

  const hasExistingScribeSubscription = useMemo(() => {
    return (
      Boolean(billingInfo?.stripe_subscription_id) &&
      !billingInfo?.is_in_reverse_free_trial &&
      billingInfo?.tier !== SubscriptionTier.FREE
    );
  }, [
    billingInfo?.stripe_subscription_id,
    billingInfo?.is_in_reverse_free_trial,
    billingInfo?.tier,
  ]);

  const hasExistingStripePaidSubscription =
    hasExistingEvidenceSubscription || hasExistingScribeSubscription;

  const cancelTarget = useMemo((): CancelTargetSubscription | null => {
    if (hasExistingEvidenceSubscription) {
      const id = billingInfo?.evidence_billing?.stripe_subscription_id;
      if (!id) return null;
      return {
        stripeSubscriptionId: id,
        // Analytics-only; funnel must not depend on new_product_tier being set.
        productTier: billingInfo?.new_product_tier ?? SubscriptionTier.EVIDENCE,
      };
    }

    if (hasExistingScribeSubscription && billingInfo?.stripe_subscription_id) {
      return {
        stripeSubscriptionId: billingInfo.stripe_subscription_id,
        productTier: billingInfo?.new_product_tier ?? billingInfo?.tier,
      };
    }

    return null;
  }, [
    billingInfo?.evidence_billing?.stripe_subscription_id,
    billingInfo?.new_product_tier,
    billingInfo?.stripe_subscription_id,
    billingInfo?.tier,
    hasExistingEvidenceSubscription,
    hasExistingScribeSubscription,
  ]);

  const redirectToSubscriptionPortal = useCallback(
    async (path: SubscriptionPortalPath, options?: RedirectSubscriptionPortalOptions) => {
      try {
        const { beforeNavigate, target } = options ?? {};
        const tier = target && 'tier' in target ? target.tier : undefined;
        const period = target && 'period' in target ? target.period : undefined;
        const price_id = target && 'price_id' in target ? target.price_id : undefined;
        const shouldUsePlanScopedPortalSession =
          path === 'update' && (price_id != null || (tier != null && period != null));
        const portalSession = shouldUsePlanScopedPortalSession
          ? (
              await withDeadline(
                getStripeLink(price_id != null ? { price_id } : { tier, period }),
                BILLING_PORTAL_SESSION_TIMEOUT_MS
              )
            ).data
          : (await withDeadline(refetchStripeData(), BILLING_PORTAL_SESSION_TIMEOUT_MS))
              .data;
        const subscriptionId = hasExistingEvidenceSubscription
          ? billingInfo?.evidence_billing?.stripe_subscription_id
          : billingInfo?.stripe_subscription_id;
        let url: string | undefined;
        if (shouldUsePlanScopedPortalSession) {
          url = portalSession?.billing_portal_url;
        } else if (portalSession?.billing_portal_url && subscriptionId) {
          url = buildBillingPortalSubscriptionDeeplink(
            portalSession.billing_portal_url,
            subscriptionId,
            path
          );
        }
        if (url) {
          // Guard is intentionally placed after the portal session fetch: the
          // network call is a read-only link retrieval, not a purchase action.
          // No billing change can occur without navigating to the URL.
          if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
          beforeNavigate?.();
          if (getCommerceBillingUiPorts().isTauri()) {
            void getCommerceBillingUiPorts().openUrlInTauri(url);
          } else {
            void router.push(url);
          }
        } else {
          toast({
            tone: 'destructive',
            title: t('account.billingPortalErrorTitle'),
            description: t('account.billingPortalErrorDescription'),
          });
        }
      } catch {
        toast({
          tone: 'destructive',
          title: t('account.billingPortalErrorTitle'),
          description: t('account.billingPortalErrorDescription'),
        });
      }
    },
    [
      refetchStripeData,
      hasExistingEvidenceSubscription,
      billingInfo?.evidence_billing?.stripe_subscription_id,
      billingInfo?.stripe_subscription_id,
      router,
      t,
    ]
  );

  const redirectToBillingPortalUpdate = useCallback(
    async (beforeNavigate?: () => void, plan?: PortalUpdateTarget) => {
      await redirectToSubscriptionPortal('update', {
        beforeNavigate,
        target: plan,
      });
    },
    [redirectToSubscriptionPortal]
  );

  const redirectToBillingPortalCancel = useCallback(
    async (beforeNavigate?: () => void) => {
      await redirectToSubscriptionPortal('cancel', { beforeNavigate });
    },
    [redirectToSubscriptionPortal]
  );

  const redirectToBillingPortalReactivate = useCallback(
    async (beforeNavigate?: () => void) => {
      await redirectToSubscriptionPortal('reactivate', { beforeNavigate });
    },
    [redirectToSubscriptionPortal]
  );

  return {
    cancelTarget,
    hasExistingStripePaidSubscription,
    isBillingInfoLoaded,
    redirectToBillingPortalUpdate,
    redirectToBillingPortalCancel,
    redirectToBillingPortalReactivate,
  };
}
